import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Star } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { useTenant } from '@core/tenant';
import { createReview, updateReview } from '../api/reviewApi';

type ReviewStep =
  | 'nps'
  | 'comments'
  | 'service'
  | 'ambience'
  | 'food'
  | 'drinks'
  | 'expectations'
  | 'returnIntent'
  | 'google'
  | 'thankyou';

const STEP_ORDER: ReviewStep[] = [
  'nps',
  'comments',
  'service',
  'ambience',
  'food',
  'drinks',
  'expectations',
  'returnIntent',
];

const ReviewPage = () => {
  const { resId, lang } = useParams<{ resId: string; lang: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('booking');
  const { restaurant } = useTenant();

  const [currentStep, setCurrentStep] = useState<ReviewStep>('nps');
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initError, setInitError] = useState(false);
  const [npsValue, setNpsValue] = useState<number | null>(null);
  const [comments, setComments] = useState('');
  const initCalled = useRef(false);

  const allowPairings =
    restaurant?.beveragePairings && restaurant.beveragePairings.length > 0;

  // Set language and create review on mount
  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang.toLowerCase());
    }
  }, [lang, i18n]);

  useEffect(() => {
    if (!resId || initCalled.current) return;
    initCalled.current = true;

    const init = async () => {
      try {
        const id = await createReview(resId);
        setReviewId(id);
      } catch {
        setInitError(true);
      }
    };
    init();
  }, [resId]);

  const handleUpdateAndAdvance = async (
    param: string,
    value: string | number
  ) => {
    if (!reviewId || isLoading) return;
    setIsLoading(true);
    try {
      await updateReview(reviewId, param, value);
      advanceStep(param);
    } catch {
      // Silently fail — legacy behavior
    } finally {
      setIsLoading(false);
    }
  };

  const advanceStep = (param: string) => {
    if (param === 'returnIntent') {
      // After returnIntent: show Google review if NPS >= 9 and placeId exists
      if (
        restaurant?.googleMapsPlaceId &&
        npsValue !== null &&
        npsValue >= 9
      ) {
        setCurrentStep('google');
      } else {
        setCurrentStep('thankyou');
      }
    } else {
      const currentIndex = STEP_ORDER.indexOf(currentStep);
      if (currentIndex < STEP_ORDER.length - 1) {
        setCurrentStep(STEP_ORDER[currentIndex + 1]);
      }
    }
  };

  const handleNpsSelect = (value: number) => {
    setNpsValue(value);
    handleUpdateAndAdvance('rating', value);
  };

  const handleCommentsSubmit = () => {
    handleUpdateAndAdvance('message', comments || '');
  };

  const handleGoToGoogle = () => {
    if (restaurant?.googleMapsPlaceId) {
      window.open(
        `https://search.google.com/local/writereview?placeid=${restaurant.googleMapsPlaceId}`,
        '_blank'
      );
    }
    setCurrentStep('thankyou');
  };

  // --- Rendering helpers ---

  const renderNpsButtons = () => {
    const values = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              disabled={isLoading}
              onClick={() => handleNpsSelect(v)}
              className={`h-11 rounded-lg border text-sm font-medium transition-colors
                ${
                  npsValue === v
                    ? 'bg-brand text-brand-foreground border-brand'
                    : 'bg-card border-border hover:border-brand/50'
                }
                disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-foreground/50 px-1">
          <span>{t('REVIEW_NOT_LIKELY')}</span>
          <span>{t('REVIEW_EXTREMELY_LIKELY')}</span>
        </div>
      </div>
    );
  };

  const renderRating5 = (
    param: string,
    _label: string
  ) => {
    const values = [1, 2, 3, 4, 5];
    return (
      <div className="space-y-4">
        <div className="flex gap-3 justify-center">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              disabled={isLoading}
              onClick={() => handleUpdateAndAdvance(param, v)}
              className="flex flex-col items-center gap-1.5 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl border flex items-center justify-center transition-colors
                  bg-card border-border group-hover:border-brand/50`}
              >
                <Star
                  size={20}
                  className="text-brand/60 group-hover:text-brand transition-colors"
                />
              </div>
              <span className="text-xs font-medium text-foreground/70">{v}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-foreground/50 px-1">
          <span>{t('REVIEW_POOR')}</span>
          <span>{t('REVIEW_GOOD')}</span>
          <span>{t('REVIEW_EXCELLENT')}</span>
        </div>
      </div>
    );
  };

  const renderScale10 = (param: string) => {
    const values = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              disabled={isLoading}
              onClick={() => handleUpdateAndAdvance(param, v)}
              className="h-11 rounded-lg border text-sm font-medium transition-colors
                bg-card border-border hover:border-brand/50
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'nps':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_NPS_QUESTION')}
            </h2>
            {renderNpsButtons()}
          </div>
        );

      case 'comments':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_COMMENTS_QUESTION')}
            </h2>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={t('REVIEW_COMMENTS_PLACEHOLDER')}
              className="w-full min-h-[120px] rounded-xl border border-border bg-card p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
            />
            <Button
              className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={handleCommentsSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : null}
              {comments
                ? t('REVIEW_CONTINUE')
                : t('REVIEW_SKIP')}
            </Button>
          </div>
        );

      case 'service':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_SERVICE_QUESTION')}
            </h2>
            {renderRating5('service', 'service')}
          </div>
        );

      case 'ambience':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_AMBIENCE_QUESTION')}
            </h2>
            {renderRating5('atmosphere', 'ambience')}
          </div>
        );

      case 'food':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_FOOD_QUESTION')}
            </h2>
            {renderRating5('food', 'food')}
          </div>
        );

      case 'drinks':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {allowPairings
                ? t('REVIEW_DRINKS_QUESTION_PAIRING')
                : t('REVIEW_DRINKS_QUESTION')}
            </h2>
            {renderRating5('drinks', 'drinks')}
          </div>
        );

      case 'expectations':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_EXPECTATIONS_QUESTION')}
            </h2>
            {renderScale10('expectations')}
            <div className="flex justify-between text-xs text-foreground/50 px-1">
              <span>{t('REVIEW_NOT_AT_ALL_WELL')}</span>
              <span>{t('REVIEW_EXTREMELY_WELL')}</span>
            </div>
          </div>
        );

      case 'returnIntent':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-display text-center leading-snug">
              {t('REVIEW_RETURN_INTENT_QUESTION')}
            </h2>
            {renderScale10('returnIntent')}
            <div className="flex justify-between text-xs text-foreground/50 px-1">
              <span>{t('REVIEW_NOT_LIKELY')}</span>
              <span>{t('REVIEW_UNDECIDED')}</span>
              <span>{t('REVIEW_EXTREMELY_LIKELY')}</span>
            </div>
          </div>
        );

      case 'google':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mb-6">
                <Star className="text-brand" size={32} />
              </div>
              <h2 className="text-lg font-display text-center leading-snug mb-4">
                {t('REVIEW_GOOGLE_TITLE')}
              </h2>
              <p className="text-sm text-foreground/60 text-center leading-relaxed">
                {t('REVIEW_GOOGLE_BODY_1')}
              </p>
              <p className="text-sm text-foreground/60 text-center leading-relaxed mt-3">
                {t('REVIEW_GOOGLE_BODY_2')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl flex-1 hover:bg-brand-dark shadow-md"
                onClick={handleGoToGoogle}
              >
                {t('REVIEW_GOOGLE_CTA')}
              </Button>
              <Button
                variant="outline"
                className="font-medium py-5 px-8 rounded-xl flex-1 border-border"
                onClick={() => setCurrentStep('thankyou')}
              >
                {t('REVIEW_FINISH')}
              </Button>
            </div>
          </div>
        );

      case 'thankyou':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle2 className="text-green-600" size={32} />
              </div>
              <h2 className="text-xl font-display text-center mb-2">
                {t('REVIEW_THANK_YOU')}
              </h2>
            </div>
            <Button
              className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('REVIEW_BACK_TO_HOME')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  // --- Step indicator ---
  const currentStepIndex = STEP_ORDER.indexOf(currentStep);
  const totalSteps = STEP_ORDER.length;
  const showProgress =
    currentStep !== 'google' && currentStep !== 'thankyou';

  // --- Main render ---
  return (
    <BookingChrome flexCol>
        <BookingHeader showLanguageSelector={false} />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1 px-6">
          {/* Init loading */}
          {!reviewId && !initError && (
            <div className="text-center text-foreground/40 text-sm py-12 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              {t('REVIEW_LOADING')}
            </div>
          )}

          {/* Init error */}
          {initError && (
            <div className="flex flex-col items-center pt-4 w-full max-w-md">
              <h2 className="text-lg font-display text-center mb-4">
                {t('REVIEW_ERROR_LOADING')}
              </h2>
              <Button
                className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
                onClick={() => navigate('/')}
              >
                {t('REVIEW_BACK_TO_HOME')}
              </Button>
            </div>
          )}

          {/* Review content */}
          {reviewId && !initError && (
            <div className="w-full max-w-md space-y-8">
              {/* Progress bar */}
              {showProgress && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-foreground/40">
                    <span>
                      {currentStepIndex + 1} / {totalSteps}
                    </span>
                  </div>
                  <div className="w-full bg-foreground/10 rounded-full h-1.5">
                    <div
                      className="bg-brand h-1.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${((currentStepIndex + 1) / totalSteps) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {renderStepContent()}
            </div>
          )}
        </main>

        <footer className="border-t border-border">
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ReviewPage;
